import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cancelEmailChange, confirmEmailChange } from '@/api/email-change';
import { Button } from '@/components/primitives/button';
import { Card, CardContent } from '@/components/primitives/card';

type Outcome = { kind: 'working' } | { kind: 'ok'; message: string } | { kind: 'error'; message: string };

/**
 * Landing page for the links emailed by the two-phase email change.
 *
 *   /auth/email-change/confirm?token=…   clicked from the NEW address
 *   /auth/email-change/cancel?token=…    clicked from the OLD address
 *
 * Deliberately does NOT require a session. Whoever proves control of the new
 * inbox may be on a device that has never signed in, and whoever needs to
 * CANCEL is likely reacting to a takeover attempt — putting a login wall in
 * front of the emergency brake would blunt the one control that has to work
 * under pressure.
 */
export function EmailChangeActionPage({ mode }: { mode: 'confirm' | 'cancel' }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'working' });
  // React 18 StrictMode double-invokes effects in dev; the token is single-use,
  // so a second call would always fail. Guard it.
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const token = params.get('token');

    if (!token) {
      setOutcome({ kind: 'error', message: 'This link is missing its token. Please use the link from your email.' });

      return;
    }

    (async () => {
      try {
        if (mode === 'confirm') {
          const { data } = await confirmEmailChange({ token });
          setOutcome({
            kind: 'ok',
            message:
              `Your email address is now ${data.email}. ` +
              (data.sessionsRevoked ? 'For your security, other sessions have been signed out.' : ''),
          });
        } else {
          const { data } = await cancelEmailChange({ token });
          setOutcome({ kind: 'ok', message: data.message });
        }
      } catch (err: unknown) {
        setOutcome({
          kind: 'error',
          message:
            err instanceof Error
              ? err.message
              : 'This link is invalid or has expired. Request a new email change from Settings.',
        });
      }
    })();
  }, [mode, params]);

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-4 p-6">
          <h1 className="text-lg font-semibold text-foreground-950">
            {mode === 'confirm' ? 'Confirming your new email' : 'Cancelling the email change'}
          </h1>

          {outcome.kind === 'working' && <p className="text-sm text-foreground-600">Working…</p>}

          {outcome.kind === 'ok' && (
            <>
              <p className="text-sm text-foreground-600">{outcome.message}</p>
              {mode === 'cancel' && (
                <p className="text-sm text-warning">
                  If you did not request this change, change your password now — someone may have access to
                  your session.
                </p>
              )}
            </>
          )}

          {outcome.kind === 'error' && <p className="text-sm text-destructive">{outcome.message}</p>}

          {outcome.kind !== 'working' && (
            <Button size="xs" onClick={() => navigate('/')}>
              Continue
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
